import { Action } from 'redux';
import { from, of } from 'rxjs';
import { catchError, filter, mergeMap } from 'rxjs/operators';
import { pricePrgError } from '../features/ui/redux/uiSlices';
import { PricePrgDependencies, RootEpic } from '../store';
import { AsyncActionCreator } from './actionUtils';

/**
 * Creates an Epic based on the supplier async actions.
 *
 * This will automatically start listening for the asyncActionCreator's request
 * and when dispatched, will fire the supplied api request, and then
 * automatically dispatch the asyncActionCreator's success or failure action
 * based on if the api request were successful or not.
 *
 * @param actionCreator
 *      the async action creator which contains the request, success, and failure action creator
 * @param apiRequest
 *      the api request to make
 * @param handleResponse
 *      dispatch additional actions on a successful response (optional)
 * @param handleFailure
 *      dispatch additional actions on a failed response (optional)
 *
 */
export function createEpicFromAsyncAction<TRequestPayload, TResponsePayload, TErrorPayload>(
    actionCreator: AsyncActionCreator<TRequestPayload, TResponsePayload, TErrorPayload>,
    apiRequest: (request: TRequestPayload, deps: PricePrgDependencies) => Promise<TResponsePayload>,
    handleResponse?: (request: TRequestPayload, response: TResponsePayload) => Action | Action[] | null,
    handleFailure?: (request: TRequestPayload, message: TErrorPayload) => Action | Action[] | null
): RootEpic {
    return (action$, _, deps) =>
        action$.pipe(
            filter(actionCreator.request.match),
            mergeMap(req =>
                from(apiRequest(req.payload as TRequestPayload, deps)).pipe(
                    mergeMap(resp => {
                        const successPayload = actionCreator.success(resp);

                        if (handleResponse == null) {
                            return of(successPayload);
                        }

                        const responseActions = handleResponse(req.payload as TRequestPayload, resp);

                        if (responseActions == null) {
                            return of(successPayload);
                        }

                        if (Array.isArray(responseActions)) {
                            return of(successPayload, ...responseActions);
                        }

                        return of(successPayload, responseActions);
                    }),
                    catchError((message: TErrorPayload) => {
                        const actions: Action<unknown>[] = [];

                        if (actionCreator.failure != null) {
                            actions.push(actionCreator.failure(message));
                        }

                        if (handleFailure != null) {
                            const catchFailureResp = handleFailure(req.payload as TRequestPayload, message);

                            if (catchFailureResp != null) {
                                if (Array.isArray(catchFailureResp)) {
                                    actions.push(...catchFailureResp);
                                } else {
                                    actions.push(catchFailureResp);
                                }
                            }
                        }

                        if (actions.length === 0) {
                            return of(pricePrgError(message));
                        }

                        return of(...actions);
                    })
                )
            )
        );
}

// Would be really good if this method could have the same signature as the one above
// but not sure how to do that

/**
 * Creates an Epic based on the supplier async actions.
 *
 * This will automatically start listening for the asyncActionCreator's request
 * and when dispatched, will fire the supplied api request, and then
 * automatically dispatch the asyncActionCreator's success or failure action
 * based on if the api request were successful or not.
 *
 * This are the same as createEpicFromAsyncAction except that the
 * request payload of the async action doesn't exist.
 *
 * @param asyncActionCreator
 *      the async action creator which contains the request, success, and failure action creator
 * @param apiRequest
 *      the api request to make
 * @param handleResponse
 *      dispatch additional actions on a successful response (optional)
 * @param handleFailure
 *      dispatch additional actions on a failed response (optional)
 *
 */
export function createEpicFromAsyncActionEmptyRequest<TResponsePayload, TErrorPayload>(
    actionCreator: AsyncActionCreator<undefined, TResponsePayload, TErrorPayload>,
    apiRequest: (deps: PricePrgDependencies) => Promise<TResponsePayload>,
    handleResponse?: (response: TResponsePayload) => Action | Action[] | null,
    handleFailure?: (message: TErrorPayload) => Action | Action[] | null
): RootEpic {
    return (action$, state$, deps) =>
        action$.pipe(
            filter(actionCreator.request.match),
            mergeMap(() =>
                from(apiRequest(deps)).pipe(
                    mergeMap(resp => {
                        const successPayload = actionCreator.success(resp);

                        if (handleResponse == null) {
                            return of(successPayload);
                        }

                        const responseActions = handleResponse(resp);

                        if (responseActions == null) {
                            return of(successPayload);
                        }

                        if (Array.isArray(responseActions)) {
                            return of(successPayload, ...responseActions);
                        }

                        return of(successPayload, responseActions);
                    }),
                    catchError((message: TErrorPayload) => {
                        const actions: Action<unknown>[] = [];

                        if (actionCreator.failure != null) {
                            actions.push(actionCreator.failure(message));
                        }

                        if (handleFailure != null) {
                            const catchFailureResp = handleFailure(message);

                            if (catchFailureResp != null) {
                                if (Array.isArray(catchFailureResp)) {
                                    actions.push(...catchFailureResp);
                                } else {
                                    actions.push(catchFailureResp);
                                }
                            }
                        }

                        if (actions.length === 0) {
                            return of(pricePrgError(message));
                        }

                        return of(...actions);
                    })
                )
            )
        );
}
